import { generatePath } from 'react-router-dom'
import { FieldSettings, TextFilter } from '@progress/kendo-react-data-tools'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { AffiliateOverviewKey } from 'api/Affiliate/AffiliateDto'
import {
  AffiliateCountryFilter,
  AffiliateTypeFilter,
  DateOnlyFilter,
} from 'components/Filter/common.filter'
import { Link } from 'components/Link'
import { GridFilterOperatorEnum, GridFilterOperatorNameEnum } from 'types/GridFilterOperatorEnum'
import { formatTimezoneAgnosticDate, formatUTCDate } from 'utils/date'
import { links } from 'utils/featureToggles'

import { AffiliateTabs } from './Details/AffiliateSlug'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

export const getAffiliateFilterOptions = (): FieldSettings[] => [
  {
    name: 'Code',
    label: 'Affiliate Code',

    filter: TextFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
      {
        text: GridFilterOperatorNameEnum.startsWithOperator,
        operator: GridFilterOperatorEnum.startsWithOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notStartsWithOperator,
        operator: GridFilterOperatorEnum.notStartsWithOperator,
      },
    ],
  },
  {
    name: 'Name',
    label: 'Name',
    filter: TextFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.startsWithOperator,
        operator: GridFilterOperatorEnum.startsWithOperator,
      },
    ],
  },
  {
    name: 'Phone',
    label: 'Phone',
    filter: TextFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
      {
        text: GridFilterOperatorNameEnum.likeOperator,
        operator: GridFilterOperatorEnum.likeOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notLikeOperator,
        operator: GridFilterOperatorEnum.notLikeOperator,
      },
    ],
  },
  {
    name: 'Email',
    label: 'Email',
    filter: TextFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: 'Country',
    label: 'Country',
    filter: AffiliateCountryFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: 'Type',
    label: 'Type',
    filter: AffiliateTypeFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: 'Birthday',
    label: 'Birthday',
    filter: DateOnlyFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
      {
        text: GridFilterOperatorNameEnum.afterOperator,
        operator: GridFilterOperatorEnum.afterOperator,
      },
      {
        text: GridFilterOperatorNameEnum.afterOrEqualOperator,
        operator: GridFilterOperatorEnum.afterOrEqualOperator,
      },
      {
        text: GridFilterOperatorNameEnum.beforeOrEqualOperator,
        operator: GridFilterOperatorEnum.beforeOrEqualOperator,
      },
      {
        text: GridFilterOperatorNameEnum.beforeOperator,
        operator: GridFilterOperatorEnum.beforeOperator,
      },
    ],
  },
  {
    name: 'Created',
    label: 'Date Created',
    filter: DateOnlyFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
      {
        text: GridFilterOperatorNameEnum.afterOperator,
        operator: GridFilterOperatorEnum.afterOperator,
      },
      {
        text: GridFilterOperatorNameEnum.afterOrEqualOperator,
        operator: GridFilterOperatorEnum.afterOrEqualOperator,
      },
      {
        text: GridFilterOperatorNameEnum.beforeOrEqualOperator,
        operator: GridFilterOperatorEnum.beforeOrEqualOperator,
      },
      {
        text: GridFilterOperatorNameEnum.beforeOperator,
        operator: GridFilterOperatorEnum.beforeOperator,
      },
    ],
  },
]

export const affiliateColumns: GridColumn[] = [
  {
    title: 'Affiliate Code',
    field: AffiliateOverviewKey.affiliateCode,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.externalId) {
        return <td />
      }

      return (
        <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
          <Link
            to={{
              pathname: generatePath(links.recordsAffiliates.detailsUrl as string, {
                id: props.dataItem.externalId,
                slug: AffiliateTabs.DETAILS,
              }),
            }}
          >
            {props.dataItem.externalId}
          </Link>
        </td>
      )
    },
    show: true,
  },
  {
    title: 'Name',
    cell: (props: GridCellProps): JSX.Element => {
      const fullName = `${props.dataItem.firstname} ${props.dataItem.lastname}`
      return <td>{fullName}</td>
    },
    show: true,
  },
  {
    title: 'Country',
    field: AffiliateOverviewKey.country,
    show: true,
  },
  {
    title: 'Type',
    field: AffiliateOverviewKey.type,
    show: true,
  },
  {
    title: 'Email Address',
    field: AffiliateOverviewKey.email,
    show: true,
  },
  {
    title: 'Phone Number',
    cell: (props: GridCellProps): JSX.Element => {
      const phoneNumber = `${props.dataItem.phoneCountryCode || ''} ${props.dataItem.phoneNumber}`
      return <td>{phoneNumber}</td>
    },
    show: true,
  },
  {
    title: 'Birthday',
    cell: (props: GridCellProps) => {
      return <td>{formatTimezoneAgnosticDate(props.dataItem.birthday, true)}</td>
    },
    show: true,
  },
  {
    title: 'Date Created',
    cell: (props: GridCellProps): JSX.Element => {
      const formattedDate = formatUTCDate(props.dataItem.createdDate)
      return <td>{formattedDate}</td>
    },
    show: true,
  },
]
